import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SocialLinks from "./SocialLinks";
import { CategoryContext } from "../context/CategoryContext";
import { useSiteConfig } from "../context/SiteConfigContext";

gsap.registerPlugin(ScrollTrigger);

// ─── Social Icons ─────────────────────────────────────────────────────────────
const InstagramIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const PinterestIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.65 7.86 6.39 9.29-.09-.78-.17-1.98.04-2.83.18-.77 1.22-5.17 1.22-5.17s-.31-.62-.31-1.55c0-1.45.84-2.53 1.89-2.53.89 0 1.32.67 1.32 1.47 0 .9-.57 2.24-.87 3.49-.25 1.04.52 1.89 1.54 1.89 1.85 0 3.1-2.36 3.1-5.15 0-2.13-1.44-3.62-3.5-3.62-2.38 0-3.78 1.79-3.78 3.63 0 .72.28 1.49.62 1.91.07.08.08.15.06.23-.06.26-.2.82-.23.94-.04.15-.13.18-.3.11-1.12-.52-1.82-2.17-1.82-3.49 0-2.84 2.06-5.45 5.94-5.45 3.12 0 5.55 2.22 5.55 5.19 0 3.1-1.95 5.59-4.66 5.59-.91 0-1.77-.47-2.06-1.03l-.56 2.1c-.2.78-.75 1.75-1.12 2.35.84.26 1.73.4 2.65.4 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </svg>
);

const YouTubeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.4a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
    <polygon
      fill="var(--color-primary, #1a0800)"
      points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"
    />
  </svg>
);

const SOCIAL_LINKS = [
  { Icon: InstagramIcon, href: "https://instagram.com", label: "Instagram" },
  { Icon: FacebookIcon, href: "https://facebook.com", label: "Facebook" },
  { Icon: PinterestIcon, href: "https://pinterest.com", label: "Pinterest" },
  { Icon: YouTubeIcon, href: "https://youtube.com", label: "YouTube" },
];

// ─── Nav Data ─────────────────────────────────────────────────────────────────
const EXPLORE_LINKS = [
  { label: "Home", to: "/" },
  // { label: "Shop", to: "/shop" },
  { label: "Collections", to: "/shop" },
  { label: "New Arrivals", to: "/new-arrivals" },
  { label: "About Us", to: "/about-us" },
];

const SUPPORT_LINKS = [
  { label: "Contact Us", to: "/contact-us" },
  { label: "Shipping & Returns", to: "/shipping" },
  // { label: "Size Guide",        to: "/size-guide" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
];

const CRAFT_BADGES = ["Handwoven", "Zardozi", "Chikankari", "Block Print"];

// ─── Component ────────────────────────────────────────────────────────────────
export default function Footer() {
  const { config } = useSiteConfig();
  const brandRef = useRef(null);
  const exploreRef = useRef(null);
  const supportRef = useRef(null);
  const newsletterRef = useRef(null);
  const bottomRef = useRef(null);

  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = () => {
    if (email.trim()) {
      setSubscribed(true);
      setEmail("");
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") handleSubscribe();
  };

  useEffect(() => {
    if (typeof gsap === "undefined") return;

    const ctx = gsap.context(() => {
      // Columns stagger
      gsap.to(
        [
          brandRef.current,
          exploreRef.current,
          supportRef.current,
          newsletterRef.current,
        ],
        {
          opacity: 1,
          y: 0,
          duration: 0.75,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: brandRef.current,
            start: "top 85%",
            once: true,
          },
        },
      );

      // Bottom bar
      gsap.to(bottomRef.current, {
        opacity: 1,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: bottomRef.current,
          start: "top 95%",
          once: true,
        },
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      <footer className="footer" role="contentinfo">
        <div className="footer-grain" aria-hidden="true" />
        <div className="footer-glow" aria-hidden="true" />

        {/* ── Top Section ── */}
        <div className="footer-top">
          <div className="container">
            <div className="row g-5">
              {/* Brand Column */}
              <div
                className="col-12 col-md-6 col-lg-3 footer-brand-col"
                ref={brandRef}
              >
                <Link
                  to="/"
                  className="footer-logo"
                >
                  <img
                    src="/images/fatimaLogo.jpeg"
                    className="img-fluid"
                    alt={config?.name}
                  ></img>
                  {/* <span className="footer-logo-dot" aria-hidden="true" /> */}
                </Link>
                <p className="footer-brand-desc">{config?.shortDescription}</p>
                {/* <div className="footer-craft-badges" aria-label="Our craft traditions">
                  {CRAFT_BADGES.map((b) => (
                    <span key={b} className="footer-craft-badge">{b}</span>
                  ))}
                </div> */}
              </div>

              {/* Explore Column */}
              <div
                className="col-6 col-md-3 col-lg-2 footer-nav-col"
                ref={exploreRef}
              >
                <h3 className="footer-col-heading">Explore</h3>
                <ul className="footer-links">
                  {EXPLORE_LINKS.map(({ label, to }) => (
                    <li key={label}>
                      <Link to={to} className="footer-link">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Customer Care Column */}
              <div
                className="col-6 col-md-3 col-lg-2 footer-nav-col"
                ref={supportRef}
              >
                <h3 className="footer-col-heading">Customer Care</h3>
                <ul className="footer-links">
                  {SUPPORT_LINKS.map(({ label, to }) => (
                    <li key={label}>
                      <Link to={to} className="footer-link">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Newsletter Column */}
              {/* Social Media Column */}
              <div
                className="col-12 col-md-6 col-lg-5 footer-newsletter-col"
                ref={newsletterRef}
              >
                <h3 className="footer-col-heading">Follow Us</h3>
                <p className="footer-newsletter-desc">
                  Stay connected with us on social media for the latest
                  collections, craft stories and exclusive offers.
                </p>
                {/* AFTER — sahi, koi ek bhi value honi chahiye tabhi render ho */}
                {Object.values(config?.socialMedia || {}).some(
                  (val) => val,
                ) && (
                  <nav
                    className="footer-social"
                    aria-label="Social media links"
                  >
                    <SocialLinks socialMedia={config?.socialMedia} />
                  </nav>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="footer-divider" role="separator" aria-hidden="true">
          <div className="footer-divider-accent" />
        </div>

        {/* ── Bottom Bar ── */}
        {/* <div className="footer-bottom" ref={bottomRef}>
          <div className="container">
            <div className="row align-items-center">

              <div className="col-12 col-md-7">
                <p className="footer-copyright">
                  All Rights Reserved &copy;&nbsp;
                  <strong>Axeperts Exhibits Pvt. Ltd</strong>
                </p>
              </div>

              <div className="col-12 col-md-5 d-flex justify-content-md-end">
                <nav className="footer-social" aria-label="Social media links">
                  {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      className="footer-social-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                    >
                      <Icon />
                    </a>
                  ))}
                </nav>
              </div>

            </div>
          </div>
        </div> */}

        <div className="footer-bottom" ref={bottomRef}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-12 col-md-6 text-start">
                <p className="footer-copyright">
                  All Rights Reserved &copy;&nbsp;
                  <strong>{config?.name}</strong>
                </p>
              </div>
              <div className=" col-12 col-md-6 d-flex justify-content-end">
                <a
                  href="https://axesell.com"
                  className="floating-designer-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="floating-content">
                    <img
                      src="/images/axesell_logo.png"
                      className="floating-icon"
                      alt="Axesell"
                    />
                  </div>
                </a>
              </div>

              {/* <div className="col-12 col-md-5 d-flex justify-content-md-end">
                <nav className="footer-social" aria-label="Social media links">
                  <SocialLinks socialMedia={config?.socialMedia} />
                </nav>
              </div> */}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
